/**
 * Widget initialization logic
 */

import type { SelectElement, WidgetState, EventHandlers } from './types';
import type { Country, Subdivision } from '@countriesdb/widget-core';
import { CountriesDBClient } from '@countriesdb/widget-core';
import {
  initializeSelect,
  populateCountrySelect,
  buildSubdivisionOptionsHTML,
  applyPreselectedValue,
  handleApiError,
  parseBoolean,
} from './dom-manipulation';
import {
  dispatchReadyEvent,
  dispatchUpdateEvent,
  isWidgetInitiatedEvent,
} from './event-system';
import {
  triggerFollowLogic,
  handleFollowRelatedFromSubdivision,
  handleFollowUpwardFromSubdivision,
  handleFollowUpwardFromCountry,
} from './follow-logic';

/**
 * Setup subdivision selection elements
 */
export async function setupSubdivisionSelection(
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  config: {
    followRelated: boolean;
    followUpward: boolean;
    showSubdivisionType: boolean;
    allowParentSelection: boolean;
    preferOfficialSubdivisions: boolean;
    subdivisionRomanizationPreference?: string;
    preferLocalVariant: boolean;
    subdivisionNameFilter?: (
      code: string,
      originalName: string,
      language: string,
      countryCode: string | null,
      item: Subdivision
    ) => string | false | null | undefined;
  }
): Promise<void> {
  const subdivisionSelects = Array.from(
    document.querySelectorAll<SelectElement>('.subdivision-selection')
  );

  for (const select of subdivisionSelects) {
    // Initialize with a default option
    initializeSelect(select, '&mdash;', true);

    // Linked country select (if any)
    const countryName = select.dataset.country;
    const linkedCountrySelect = countryName
      ? document.querySelector<SelectElement>(
          `.country-selection[data-name="${countryName}"]`
        )
      : null;

    // Check if linked country select is multi-select (not allowed)
    if (linkedCountrySelect && linkedCountrySelect.hasAttribute('multiple')) {
      const defaultValue = select.dataset.defaultValue ?? '';
      select.innerHTML = `<option value="${defaultValue}" disabled>Error: Cannot link to multi-select country. Use data-country-code instead.</option>`;
      continue;
    }

    // No direct link → maybe data-country-code
    if (!countryName || !linkedCountrySelect) {
      if (
        select.hasAttribute('data-country-code') &&
        select.dataset.countryCode
      ) {
        await updateSubdivisionSelect(
          select,
          apiKey,
          backendUrl,
          state,
          config,
          select.dataset.countryCode
        );
      } else {
        const defaultValue = select.dataset.defaultValue ?? '';
        select.innerHTML += `<option value="${defaultValue}" disabled>Error: No country select present</option>`;
      }
    }

    // Remove old event handlers if they exist (for re-initialization)
    const oldHandlers = state.eventHandlers.get(select);
    if (oldHandlers) {
      if (oldHandlers.update) {
        select.removeEventListener('change', oldHandlers.update);
      }
      if (oldHandlers.followRelated) {
        select.removeEventListener('change', oldHandlers.followRelated);
      }
      if (oldHandlers.followUpward) {
        select.removeEventListener('change', oldHandlers.followUpward);
      }
    }

    // Create new event handlers
    const handlers: EventHandlers = {};

    // Always dispatch an update event for user-initiated subdivision changes
    handlers.update = (event: Event) => {
      if (isWidgetInitiatedEvent(event)) {
        return;
      }
      dispatchUpdateEvent(select, { type: 'subdivision', reason: 'regular' });
    };
    select.addEventListener('change', handlers.update);

    // --- follow_related (forward direction) ---
    handlers.followRelated = async (event: Event) => {
      if (isWidgetInitiatedEvent(event)) {
        return;
      }
      await handleFollowRelatedFromSubdivision(
        select,
        apiKey,
        backendUrl,
        state,
        config.followRelated,
        (s, key, code) =>
          updateSubdivisionSelect(s, key, backendUrl, state, config, code)
      );
    };
    select.addEventListener('change', handlers.followRelated);

    // --- follow_upward (reverse direction) ---
    handlers.followUpward = async (event: Event) => {
      if (isWidgetInitiatedEvent(event)) {
        return;
      }
      await handleFollowUpwardFromSubdivision(
        select,
        apiKey,
        backendUrl,
        state,
        config.followUpward,
        (s, key, code) =>
          updateSubdivisionSelect(s, key, backendUrl, state, config, code)
      );
    };
    select.addEventListener('change', handlers.followUpward);

    // Store handlers for future cleanup
    state.eventHandlers.set(select, handlers);
  }
}

/**
 * Update subdivision select with data for a specific country
 */
export async function updateSubdivisionSelect(
  select: SelectElement,
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  config: {
    followRelated: boolean;
    followUpward: boolean;
    showSubdivisionType: boolean;
    allowParentSelection: boolean;
    preferOfficialSubdivisions: boolean;
    subdivisionRomanizationPreference?: string;
    preferLocalVariant: boolean;
    forcedLanguage?: string;
    defaultLanguage?: string;
    subdivisionNameFilter?: (
      code: string,
      originalName: string,
      language: string,
      countryCode: string | null,
      item: Subdivision
    ) => string | false | null | undefined;
  },
  countryCode: string
): Promise<void> {
  const preselectedValue =
    select.getAttribute('data-preselected') || select.dataset.preselected;

  // Check if this is a reload (select was already populated before)
  const isReload = state.subdivisionsMap.has(select);

  initializeSelect(select, '&mdash;', true);

  // Mark as initializing to prevent change events
  state.isInitializing.add(select);

  let effectiveCountryCode = countryCode;
  if (!effectiveCountryCode) {
    effectiveCountryCode = select.dataset.countryCode || '';
  }

  if (effectiveCountryCode) {
    let valueSetByWidget = false; // Track if a value was set by widget
    try {
      // Use GeoIP only if data-preselected attribute is not set at all
      const shouldUseGeoIP =
        preselectedValue === undefined || preselectedValue === null;

      // Check if this subdivision select prefers official subdivisions
      // Use data attribute if present, otherwise use config
      const preferOfficial = select.hasAttribute('data-prefer-official') 
        ? true 
        : config.preferOfficialSubdivisions;

      const languageHeaders = CountriesDBClient.getLanguageHeaders(
        config.forcedLanguage,
        config.defaultLanguage
      );

      const subdivisionsResult = await CountriesDBClient.fetchSubdivisions({
        apiKey,
        backendUrl,
        countryCode: effectiveCountryCode,
        shouldUseGeoIP,
        preferOfficial,
        subdivisionRomanizationPreference: config.subdivisionRomanizationPreference,
        preferLocalVariant: config.preferLocalVariant,
        languageHeaders,
      });

      const subdivisions = subdivisionsResult.data;
      const subdivisionsLanguage = subdivisionsResult.language || 'en';

      // Store in memory
      state.subdivisionsMap.set(select, subdivisions);
      state.subdivisionsLanguageMap.set(select, subdivisionsLanguage);

      if (subdivisions.length > 0) {
        // Populate <option>
        select.disabled = false;

        // Preserve data attributes before setting innerHTML (only for non-multi-select)
        const isMultiple = select.hasAttribute('multiple');
        const dataLabel = !isMultiple ? select.dataset.label : undefined;
        const dataDefaultValue = !isMultiple
          ? select.dataset.defaultValue
          : undefined;

        // Preserve user's current selection before clearing innerHTML
        // This prevents preselected values from overwriting user selections
        const currentValue = select.value;
        const defaultValue = select.dataset.defaultValue || '';
        const hasUserSelection = currentValue && currentValue !== defaultValue && currentValue.trim() !== '';

        select.innerHTML = buildSubdivisionOptionsHTML(
          subdivisions,
          select,
          subdivisionsLanguage,
          config.showSubdivisionType,
          config.allowParentSelection,
          config.subdivisionNameFilter
        );

        // Restore data attributes after setting innerHTML
        if (!isMultiple) {
          if (dataLabel !== undefined) {
            select.dataset.label = dataLabel;
          }
          if (dataDefaultValue !== undefined) {
            select.dataset.defaultValue = dataDefaultValue;
          }
        }

        // Restore user's selection if it exists in the new options (user selection takes priority)
        let userSelectionRestored = false;
        if (hasUserSelection && !isMultiple) {
          const optionExists = Array.from(select.options).some(opt => opt.value === currentValue);
          if (optionExists) {
            select.value = currentValue;
            userSelectionRestored = true;
            // Don't dispatch event here - user already selected it, no need to notify again
          }
        }

        // Manual preselection only if user hasn't selected anything
        if (!userSelectionRestored) {
          // Check if preselected value exists (applyPreselectedValue will read it from select element)
          const hasPreselectedValue = 
            (select.getAttribute('data-preselected') || select.dataset.preselected || select.dataset._widgetTempPreselect) !== undefined;
          const preselectedConsumed = select.dataset._widgetPreselectedConsumed === '1';
          
          // Only apply preselected if:
          // 1. It exists AND
          // 2. Either it hasn't been consumed yet, OR this is the initial load (not a reload)
          // This prevents reapplying preselected values on user-initiated country changes
          // But allows them on initial load and on reload: true (which clears the consumed flag)
          if (hasPreselectedValue && (!preselectedConsumed || !isReload)) {
            applyPreselectedValue(select, apiKey);
            dispatchUpdateEvent(select, {
              type: 'subdivision',
              reason: 'preselected',
            });
            valueSetByWidget = true;
            await triggerFollowLogic(
              select,
              apiKey,
              backendUrl,
              state,
              config.followRelated,
              config.followUpward,
              (s, key, code) =>
                updateSubdivisionSelect(s, key, backendUrl, state, config, code),
              (countrySelect, key) =>
                updateSubdivisions(countrySelect, key, backendUrl, state, config)
            );
          } else {
            // Try GeoIP preselect (only if user hasn't selected anything)
            if (shouldUseGeoIP) {
              const preselectedSubdivision = subdivisions.find(
                (subdivision) => subdivision.preselected
              );
              if (preselectedSubdivision) {
                const isMultiple = select.hasAttribute('multiple');
                let subdivisionToSelect = preselectedSubdivision;
                
                // If preselected is disabled and allowParentSelection is false, find first enabled child
                if (!config.allowParentSelection && !isMultiple) {
                  const preselectedOption = Array.from(select.options).find(
                    (opt) => opt.value === preselectedSubdivision.code
                  );
                  
                  if (preselectedOption?.disabled) {
                    // Find direct children by parent_id
                    const directChildren = subdivisions.filter(
                      (sub) => sub.parent_id === preselectedSubdivision.id
                    );
                    
                    if (directChildren.length > 0) {
                      // Sort by name and get first child
                      const sorted = directChildren.sort((a, b) => {
                        return (a.name || '').localeCompare(b.name || '', subdivisionsLanguage, { sensitivity: 'accent' });
                      });
                      
                      const firstChild = sorted[0];
                      
                      // Check if first child is also disabled (has its own children)
                      const firstChildOption = Array.from(select.options).find(
                        (opt) => opt.value === firstChild.code
                      );
                      
                      if (firstChildOption?.disabled) {
                        // First child is disabled, find its first child (grandchild)
                        const grandChildren = subdivisions.filter(
                          (sub) => sub.parent_id === firstChild.id
                        );
                        if (grandChildren.length > 0) {
                          const sortedGrandChildren = grandChildren.sort((a, b) => {
                            return (a.name || '').localeCompare(b.name || '', subdivisionsLanguage, { sensitivity: 'accent' });
                          });
                          subdivisionToSelect = sortedGrandChildren[0];
                        } else {
                          subdivisionToSelect = firstChild;
                        }
                      } else {
                        subdivisionToSelect = firstChild;
                      }
                    }
                  }
                }
                
                if (isMultiple) {
                  // For multi-select, find and select the option
                  const option = Array.from(select.options).find(
                    (opt) => opt.value === subdivisionToSelect.code
                  );
                  if (option) {
                    option.selected = true;
                  }
                } else {
                  // Single select: set value directly
                  select.value = subdivisionToSelect.code;
                }
                dispatchUpdateEvent(select, {
                  type: 'subdivision',
                  reason: 'geoip',
                });
                valueSetByWidget = true;
                await triggerFollowLogic(
                  select,
                  apiKey,
                  backendUrl,
                  state,
                  config.followRelated,
                  config.followUpward,
                  (s, key, code) =>
                    updateSubdivisionSelect(s, key, backendUrl, state, config, code),
                  (countrySelect, key) =>
                    updateSubdivisions(countrySelect, key, backendUrl, state, config)
                );
              }
            }
          }
        }
      } else {
        select.disabled = true;
      }
    } catch (error) {
      console.error('Failed to fetch subdivisions:', error);
      handleApiError(select, error as Error);
    } finally {
      // Mark initialization as complete
      state.isInitializing.delete(select);
      dispatchReadyEvent(select, {
        type: 'subdivision',
        phase: isReload ? 'reload' : 'initial',
      });
      // Only fire 'reload' if this is a reload, not initial load
      if (isReload && !valueSetByWidget) {
        dispatchUpdateEvent(select, {
          type: 'subdivision',
          reason: 'reload',
        });
      }
    }
  } else if (
    !select.dataset.country ||
    !document.querySelector(
      `.country-selection[data-name="${select.dataset.country}"]`
    )
  ) {
    const defaultValue = select.dataset.defaultValue ?? '';
    select.innerHTML += `<option value="${defaultValue}" disabled>Error: No country select present</option>`;
  }
}

/**
 * Get current subdivision config from window.CountriesDBConfig and script URL
 * This ensures we always use the latest config values, even after widget reload
 */
function getCurrentSubdivisionConfig(apiKey: string, backendUrl: string): {
  followRelated: boolean;
  followUpward: boolean;
  showSubdivisionType: boolean;
  allowParentSelection: boolean;
  preferOfficialSubdivisions: boolean;
  subdivisionRomanizationPreference?: string;
  preferLocalVariant: boolean;
  forcedLanguage?: string;
  defaultLanguage?: string;
  subdivisionNameFilter?: (
    code: string,
    originalName: string,
    language: string,
    countryCode: string | null,
    item: Subdivision
  ) => string | false | null | undefined;
} {
  const globalConfig = typeof window !== 'undefined' && (window as any).CountriesDBConfig
    ? (window as any).CountriesDBConfig
    : null;

  // Also check script URL parameters (for backward compatibility and reload scenarios)
  let scriptUrl: URL | null = null;
  try {
    const scripts = Array.from(document.getElementsByTagName('script'));
    const loaderScript = scripts.find((s) => 
      s.src && (
        s.src.includes('@countriesdb/widget') ||
        s.src.includes('widget/dist/index.js')
      )
    ) as HTMLScriptElement || null;
    
    if (loaderScript && loaderScript.src) {
      scriptUrl = new URL(loaderScript.src);
    }
  } catch {
    // Ignore errors
  }

  // Import parseBoolean from dom-manipulation
  const parseBoolean = (value: string | null | undefined): boolean => {
    if (value === null || value === undefined) {
      return false;
    }
    const lowered = String(value).trim().toLowerCase();
    return !(lowered === '0' || lowered === 'false');
  };

  return {
    followRelated: globalConfig?.followRelated !== undefined
      ? globalConfig.followRelated
      : parseBoolean(scriptUrl?.searchParams.get('follow_related') ?? 'false'),
    followUpward: globalConfig?.followUpward !== undefined
      ? globalConfig.followUpward
      : parseBoolean(scriptUrl?.searchParams.get('follow_upward') ?? 'false'),
    showSubdivisionType: globalConfig?.showSubdivisionType !== undefined
      ? globalConfig.showSubdivisionType
      : parseBoolean(scriptUrl?.searchParams.get('show_subdivision_type') ?? '1'),
    allowParentSelection: globalConfig?.allowParentSelection !== undefined
      ? globalConfig.allowParentSelection
      : parseBoolean(scriptUrl?.searchParams.get('allow_parent_selection') ?? 'false'),
    preferOfficialSubdivisions: globalConfig?.preferOfficialSubdivisions !== undefined
      ? globalConfig.preferOfficialSubdivisions
      : parseBoolean(scriptUrl?.searchParams.get('prefer_official') ?? 'false'),
    subdivisionRomanizationPreference: globalConfig?.subdivisionRomanizationPreference ||
      scriptUrl?.searchParams.get('subdivision_romanization_preference') ||
      undefined,
    preferLocalVariant: globalConfig?.preferLocalVariant !== undefined
      ? globalConfig.preferLocalVariant
      : parseBoolean(scriptUrl?.searchParams.get('prefer_local_variant') ?? 'false'),
    forcedLanguage: globalConfig?.forcedLanguage ||
      scriptUrl?.searchParams.get('forced_language') ||
      undefined,
    defaultLanguage: globalConfig?.defaultLanguage ||
      scriptUrl?.searchParams.get('default_language') ||
      undefined,
    subdivisionNameFilter: globalConfig?.subdivisionNameFilter,
  };
}

/**
 * Update subdivisions for all linked subdivision selects when country changes
 */
export async function updateSubdivisions(
  countrySelect: SelectElement,
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  config: {
    followRelated: boolean;
    followUpward: boolean;
    showSubdivisionType: boolean;
    allowParentSelection: boolean;
    preferOfficialSubdivisions: boolean;
    subdivisionRomanizationPreference?: string;
    preferLocalVariant: boolean;
    forcedLanguage?: string;
    defaultLanguage?: string;
    subdivisionNameFilter?: (
      code: string,
      originalName: string,
      language: string,
      countryCode: string | null,
      item: Subdivision
    ) => string | false | null | undefined;
  }
): Promise<void> {
  // Don't update subdivisions for multi-select countries (not supported)
  if (countrySelect.hasAttribute('multiple')) {
    return;
  }

  const selectedCountry = countrySelect.value;
  const linkedSubdivisionSelects = Array.from(
    document.querySelectorAll<SelectElement>(
      `.subdivision-selection[data-country="${countrySelect.dataset.name}"]`
    )
  );

  for (const select of linkedSubdivisionSelects) {
    await updateSubdivisionSelect(
      select,
      apiKey,
      backendUrl,
      state,
      config,
      selectedCountry
    );
  }
}

/**
 * Setup country selection elements
 */
export async function setupCountrySelection(
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  config: {
    defaultLanguage?: string;
    forcedLanguage?: string;
    isoCountryNames: boolean;
    followRelated: boolean;
    followUpward: boolean;
    countryNameFilter?: (
      code: string,
      name: string,
      language: string,
      item: Country
    ) => string | false | null | undefined;
  },
  subdivisionConfig: {
    followRelated: boolean;
    followUpward: boolean;
    showSubdivisionType: boolean;
    allowParentSelection: boolean;
    preferOfficialSubdivisions: boolean;
    subdivisionRomanizationPreference?: string;
    preferLocalVariant: boolean;
    subdivisionNameFilter?: (
      code: string,
      originalName: string,
      language: string,
      countryCode: string | null,
      item: Subdivision
    ) => string | false | null | undefined;
  }
): Promise<void> {
  const countrySelects = Array.from(
    document.querySelectorAll<SelectElement>('.country-selection')
  );
  const seenNames: Record<string, boolean> = {}; // track data-name to detect duplicates

  for (const select of countrySelects) {
    const name = select.dataset.name;

    // Duplicates
    if (name && seenNames[name]) {
      select.removeAttribute('data-name');
      initializeSelect(select, '&mdash;');
      const defaultValue = select.dataset.defaultValue ?? '';
      select.innerHTML += `<option value="${defaultValue}" disabled>Error: Duplicate field</option>`;
      continue;
    }
    if (name) {
      seenNames[name] = true;
    }

    // Note: Class renaming is now handled earlier in fixMisClassedElements()
    // This check is kept as a safety net but should not be needed

    // Initialize
    initializeSelect(select, '&mdash;');

    // Mark as initializing to prevent change events
    state.isInitializing.add(select);

    let valueSetByWidget = false; // Track if value was set by widget
    let loadedInitialSubdivisions = false; // Track if subdivisions were loaded

    try {
      const preselectedValue =
        select.getAttribute('data-preselected') || select.dataset.preselected;
      // Use GeoIP only if data-preselected attribute is not set at all
      const shouldUseGeoIP =
        preselectedValue === undefined || preselectedValue === null;

      const languageHeaders = CountriesDBClient.getLanguageHeaders(
        config.forcedLanguage,
        config.defaultLanguage
      );

      // Fetch & populate countries
      const countriesResult = await CountriesDBClient.fetchCountries({
        apiKey,
        backendUrl,
        shouldUseGeoIP,
        isoCountryNames: config.isoCountryNames,
        languageHeaders,
      });

      const countries = countriesResult.data;
      const countriesLanguage = countriesResult.language || 'en';

      state.countriesMap.set(select, countries);
      populateCountrySelect(
        select,
        countries,
        countriesLanguage,
        config.countryNameFilter
      );

      // Apply preselected (manual)
      // Check if preselected value exists (applyPreselectedValue will read it from select element)
      const hasPreselectedValue = 
        (select.getAttribute('data-preselected') || select.dataset.preselected || select.dataset._widgetTempPreselect) !== undefined;
      if (hasPreselectedValue) {
        applyPreselectedValue(select, apiKey);
        dispatchUpdateEvent(select, { type: 'country', reason: 'preselected' });
        valueSetByWidget = true;
        // Load subdivisions after applying preselected value
        if (
          select.value &&
          select.value !== (select.dataset.defaultValue || '')
        ) {
          // This will be handled by the updateSubdivisions call below
          // We need to pass the config for subdivisions
        }
      }

      // GeoIP auto-select
      if (shouldUseGeoIP) {
        const preselectedCountry = countries.find(
          (country) => country.preselected
        );
        if (preselectedCountry) {
          select.value = preselectedCountry.iso_alpha_2;
          dispatchUpdateEvent(select, { type: 'country', reason: 'geoip' });
          valueSetByWidget = true;
        }
      }

      // If already chosen, load subdivisions
      if (
        !loadedInitialSubdivisions &&
        select.value &&
        select.value !== (select.dataset.defaultValue || '')
      ) {
        // Subdivisions will be loaded by event handler
        loadedInitialSubdivisions = true;
      }

      // Remove old event handlers if they exist (for re-initialization)
      const oldCountryHandlers = state.eventHandlers.get(select);
      if (oldCountryHandlers?.countryChange) {
        select.removeEventListener('change', oldCountryHandlers.countryChange);
      }

      // Create new country change handler
      const countryChangeHandler = async (event: Event) => {
        if (isWidgetInitiatedEvent(event)) {
          return;
        }

        // Dispatch update event for user-initiated country change
        dispatchUpdateEvent(select, { type: 'country', reason: 'regular' });

        // Get current config dynamically to ensure we use the latest values
        const currentSubdivisionConfig = getCurrentSubdivisionConfig(apiKey, backendUrl);
        const currentConfig = typeof window !== 'undefined' && (window as any).CountriesDBConfig
          ? (window as any).CountriesDBConfig
          : config;

        // Update subdivisions with current config
        await updateSubdivisions(select, apiKey, backendUrl, state, currentSubdivisionConfig);

        const chosen = select.value;
        if (!chosen) {
          return;
        }

        const stored = state.countriesMap.get(select) || [];
        const picked = stored.find((c) => c.iso_alpha_2 === chosen);
        if (!picked) {
          return;
        }

        // followUpward from country perspective
        // Only works when country is single-select (never for multi-select)
        const currentFollowUpward = currentConfig?.followUpward || false;
        if (currentFollowUpward && !select.multiple && picked.is_subdivision_of) {
          await handleFollowUpwardFromCountry(
            select,
            apiKey,
            backendUrl,
            state,
            currentFollowUpward,
            (s, key, code) =>
              updateSubdivisionSelect(s, key, backendUrl, state, currentSubdivisionConfig, code)
          );
        }
      };

      // Store and attach the handler
      const countryHandlers = state.eventHandlers.get(select) || {};
      countryHandlers.countryChange = countryChangeHandler;
      state.eventHandlers.set(select, countryHandlers);
      select.addEventListener('change', countryChangeHandler);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      handleApiError(select, error as Error);
      // Handle subdivision errors
      if (select.dataset.name) {
        const linkedSubdivisionSelects = Array.from(
          document.querySelectorAll<SelectElement>(
            `.subdivision-selection[data-country="${select.dataset.name}"]`
          )
        );
        for (const s of linkedSubdivisionSelects) {
          initializeSelect(s, '&mdash;');
          handleApiError(s, error as Error);
        }
      }
    } finally {
      // Mark initialization as complete
      state.isInitializing.delete(select);
      dispatchReadyEvent(select, {
        type: 'country',
        phase: 'initial',
      });
      // If no preselected and no geoip selection happened, emit a regular update
      if (!valueSetByWidget) {
        dispatchUpdateEvent(select, { type: 'country', reason: 'regular' });
      }
    }
  }
}

